import type { ReactNode } from 'react';

import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/shared/i18n';
import { ErrorState, LoadingState } from '@/shared/ui';

import { useEvotorIntegrationSettings, useUpdateEvotorIntegrationSettings } from '../../hooks';
import { useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type {
  EvotorIntegrationStep,
  UpdateEvotorIntegrationStepRequest,
} from '../../model/settingsTypes';
import { EVOTOR_INTEGRATION_STEP_OPTIONS } from '../options';
import { FeedbackSnackbar } from './FeedbackSnackbar';
import { FieldRow } from './FieldRow';
import { SettingsCard } from './SettingsCard';

/** Loosely-typed to match react-i18next's `t` without dragging in the full `TFunction` generics. */
type Translate = (key: string, vars?: Record<string, unknown>) => string;

const EVOTOR_STEP_KEY_ORDER = EVOTOR_INTEGRATION_STEP_OPTIONS.map((option) => option.value as string);
const EVOTOR_STEP_OPTION_BY_KEY = new Map(
  EVOTOR_INTEGRATION_STEP_OPTIONS.map((option) => [option.value as string, option]),
);

/**
 * Known steps first, in the BFF-documented order, then any server-returned step whose key isn't
 * in that known set — appended after, in the order the server sent them (tolerates unknown keys
 * without guessing where they belong).
 */
function orderEvotorIntegrationSteps(steps: EvotorIntegrationStep[]): EvotorIntegrationStep[] {
  const byKey = new Map(steps.map((step) => [step.key, step]));
  const known = EVOTOR_STEP_KEY_ORDER.map((key) => byKey.get(key)).filter(
    (step): step is EvotorIntegrationStep => Boolean(step),
  );
  const unknown = steps.filter((step) => !EVOTOR_STEP_KEY_ORDER.includes(step.key));
  return [...known, ...unknown];
}

/**
 * A known key's translated label, or the raw key itself for a step the BFF hasn't documented yet
 * — per spec, an unknown step key is tolerated by rendering "with the raw key as label", not a
 * humanised guess (the known 10 keys are the only ones with a real translated label).
 */
function evotorStepLabel(key: string, t: Translate): string {
  const option = EVOTOR_STEP_OPTION_BY_KEY.get(key);
  return option ? t(option.label) : key;
}

function formatEvotorUpdatedAt(value: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(
    date,
  );
}

interface EvotorIntegrationStepRowProps {
  step: EvotorIntegrationStep;
  label: string;
  disabled: boolean;
  onToggle: (key: string, enabled: boolean) => void;
}

/**
 * One sync-pipeline step: a switch bound to tenant intent (`enabled`, always interactive for a
 * manager regardless of the platform switch), a platform-disabled warning chip next to the label
 * when `platformEnabled` is false, and the effective state always shown as plain secondary text.
 */
function EvotorIntegrationStepRow({ step, label, disabled, onToggle }: EvotorIntegrationStepRowProps) {
  const { t } = useTranslation('settings');
  return (
    <FieldRow
      label={
        step.platformEnabled ? (
          label
        ) : (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <span>{label}</span>
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              label={t('retailSettings.automation.stepsSection.platformDisabledChip')}
            />
          </Stack>
        )
      }
      description={t('retailSettings.automation.stepsSection.effectiveLabel', {
        state: step.effectiveEnabled
          ? t('retailSettings.automation.stepsSection.effectiveOn')
          : t('retailSettings.automation.stepsSection.effectiveOff'),
      })}
    >
      <Switch
        checked={step.enabled}
        disabled={disabled}
        onChange={(event) => onToggle(step.key, event.target.checked)}
        slotProps={{ input: { 'aria-label': label } }}
      />
    </FieldRow>
  );
}

export interface RetailEvotorAutomationPanelProps {
  /** Whether the Автоматизация tab's own intro copy should mention it is where regular sync turns on. */
  active: boolean;
}

/**
 * Автоматизация tab — tenant-intent toggles for the platform-wide Evotor↔OBZR sync pipeline's
 * steps (formerly the standalone `EvotorIntegrationSettingsPage`, `/settings/integrations/evotor`,
 * now this tab). This is the "включаем регулярные интеграции" step of the merchant's journey.
 *
 * Gated independently from the rest of the Ritail page: `integrations.evotor.view`/`.manage` are
 * genuinely separate permission codes from `settings.retail-evotor.*` (the `settings.*` wildcard
 * does not cover them — see `model/permissions.ts`), so this tab owns its own compact
 * permission/load/error/save-bar presentation rather than nesting a second `SettingsScaffold`
 * (which bakes in a back-link + page title unsuitable for a tab embedded in an already-chromed
 * page). A user with `settings.retail-evotor.view` but not `integrations.evotor.view` sees a
 * clean "access restricted" state here while the other five tabs stay fully usable.
 *
 * Optimistic-free: the PUT response (the BFF's own fresh resource) is what lands in the cache,
 * never a locally-predicted value.
 */
export function RetailEvotorAutomationPanel({ active }: RetailEvotorAutomationPanelProps) {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('evotor-integration');
  // Only fetch while this tab both exists and is actually viewable — `active` also lets the panel
  // stay mounted-but-idle if a future caller ever keeps tabs alive across switches.
  const query = useEvotorIntegrationSettings({ enabled: active && canView });
  const update = useUpdateEvotorIntegrationSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleStepToggle = (key: string, enabled: boolean) => {
    if (!form.draft) {
      return;
    }
    form.setField(
      'steps',
      form.draft.steps.map((step) => (step.key === key ? { ...step, enabled } : step)),
    );
  };

  const handleSave = () => {
    const draft = form.draft;
    const baseline = query.data;
    if (!draft || !baseline) {
      return;
    }
    const changedSteps: UpdateEvotorIntegrationStepRequest[] = draft.steps
      .filter((step) => {
        const original = baseline.steps.find((candidate) => candidate.key === step.key);
        return original ? original.enabled !== step.enabled : false;
      })
      .map((step) => ({ key: step.key, enabled: step.enabled }));

    if (changedSteps.length === 0) {
      return;
    }

    update.mutate(
      { steps: changedSteps },
      {
        onSuccess: () => success(t('retailSettings.automation.savedFeedback')),
        onError: (error) => notifyError(error.message),
      },
    );
  };

  const dirty = form.dirty;
  const saving = update.isPending;
  const showSaveBar = canView && canManage && query.isSuccess;

  let body: ReactNode;
  if (!canView) {
    body = (
      <ErrorState
        title={t('scaffold.accessRestrictedTitle')}
        message={t('scaffold.accessRestrictedMessage')}
      />
    );
  } else if (query.isLoading) {
    body = <LoadingState label={t('scaffold.loading')} fullHeight={false} />;
  } else if (query.isError) {
    // Mirrors `SettingsScaffold`'s own owner-unavailable handling (epic-001 AC9) — this tab
    // doesn't nest `SettingsScaffold` (see the doc comment above), so it replicates just this one
    // piece of shared behaviour rather than silently losing it.
    const isOwnerUnavailable =
      query.error?.status === 503 || query.error?.code === 'owner-unavailable';
    body = (
      <ErrorState
        title={isOwnerUnavailable ? t('scaffold.serviceUnavailableTitle') : t('scaffold.loadErrorTitle')}
        message={
          isOwnerUnavailable
            ? t('scaffold.serviceUnavailableMessage')
            : (query.error?.message ?? t('common:error.unknown'))
        }
        onRetry={() => void query.refetch()}
      />
    );
  } else if (query.isSuccess) {
    const draft = form.draft;
    const data = query.data;
    if (!draft) {
      body = null;
    } else {
      const orderedSteps = orderEvotorIntegrationSteps(draft.steps);
      const formattedUpdatedAt = formatEvotorUpdatedAt(data.updatedAt);

      let footerText: string;
      if (!formattedUpdatedAt) {
        footerText = t('retailSettings.automation.footer.neverUpdated');
      } else if (data.updatedBy) {
        footerText = t('retailSettings.automation.footer.updatedWithActor', {
          value: formattedUpdatedAt,
          actor: data.updatedBy,
        });
      } else {
        footerText = t('retailSettings.automation.footer.updatedNoActor', {
          value: formattedUpdatedAt,
        });
      }

      body = (
        <Stack spacing={2.5}>
          {!canManage ? (
            <Alert severity="info">{t('scaffold.readOnlyNotice')}</Alert>
          ) : null}
          <SettingsCard
            title={t('retailSettings.automation.stepsSection.title')}
            description={t('retailSettings.automation.stepsSection.description')}
            icon={<SyncRoundedIcon />}
          >
            {orderedSteps.map((step) => (
              <EvotorIntegrationStepRow
                key={step.key}
                step={step}
                label={evotorStepLabel(step.key, t)}
                disabled={!canManage}
                onToggle={handleStepToggle}
              />
            ))}
            <FieldRow
              label={t('retailSettings.automation.stepsSection.catalogPublicationModeLabel')}
              description={t('retailSettings.automation.stepsSection.catalogPublicationModeHint')}
            >
              <Chip
                size="small"
                variant="outlined"
                label={
                  data.catalogPublicationMode === 'DISABLED'
                    ? t('retailSettings.automation.stepsSection.catalogPublicationModeDisabled')
                    : data.catalogPublicationMode
                }
              />
            </FieldRow>
          </SettingsCard>

          <Typography variant="caption" color="text.secondary">
            {footerText}
          </Typography>
        </Stack>
      );
    }
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 720 }}>
        {t('retailSettings.automation.intro')}
      </Typography>

      {body}

      {showSaveBar ? (
        <Paper
          sx={{
            position: 'sticky',
            bottom: 16,
            mt: 3,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            boxShadow: '0 8px 28px rgba(16, 24, 40, 0.12)',
          }}
        >
          <Typography
            variant="body2"
            color={dirty ? 'text.primary' : 'text.secondary'}
            sx={{ flexGrow: 1, pl: 1 }}
          >
            {dirty ? t('scaffold.unsavedChanges') : t('scaffold.allChangesSaved')}
          </Typography>
          <Button onClick={form.reset} disabled={!dirty || saving}>
            {t('common:action.reset')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!dirty || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
          >
            {saving ? t('common:status.saving') : t('scaffold.saveLabel')}
          </Button>
        </Paper>
      ) : null}

      <FeedbackSnackbar feedback={feedback} onClose={clear} />
    </Box>
  );
}
